import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  ArrowBack as ArrowBackIcon,
  EventAvailable as EventAvailableIcon,
  EventRepeat as FrequencyIcon,
  EventBusy as NoShowIcon,
  LocalHospital as ConsultationIcon,
  Payments as LifetimeIcon,
  ShoppingBag as ShoppingBagIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  TrendingUp as RecentIcon,
  Undo as RefundIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import ManagementPageLayout, { ManagementPageCard } from '../../components/layout/ManagementPageLayout';
import ManagementPageSkeleton from '../../components/layout/ManagementPageSkeleton';
import PrimaryActionButton from '../../components/common/PrimaryActionButton';
import StoreCustomerMetricCard from '../../components/customers/StoreCustomerMetricCard';
import StoreCustomerSectionCard from '../../components/customers/StoreCustomerSectionCard';
import StoreCustomerUpcomingAppointments from '../../components/customers/StoreCustomerUpcomingAppointments';
import StoreCustomerProfileDialog from '../../components/customers/StoreCustomerProfileDialog';
import StoreCustomerRefunds from '../../components/customers/StoreCustomerRefunds';
import StoreCustomerServicePackagesCard from '../../components/customers/StoreCustomerServicePackagesCard';
import StoreCustomerConsentsCard from '../../components/customers/StoreCustomerConsentsCard';
import { useAppData } from '../../context/AppDataContext';
import { useToast } from '../../context/ToastContext';
import usePermissions from '../../hooks/usePermissions';
import useStorePlan from '../../hooks/useStorePlan';
import { Permissions } from '../../constants/Permissions';
import StoreCustomerService from '../../services/storeCustomerService';
import requestDeduplicator from '../../utils/requestDeduplicator';
import { formatCalendarDate, formatUtcDate, getBookingSlotOneHourAhead } from '../../utils/dateFormatUtils';
import { formatCurrency, DEFAULT_CURRENCY } from '../../utils/currencyUtils';
import { buildManagementAppointmentsPath, getSafeManagementReturnPath } from '../../utils/staffNotificationNavigation';
import { BRAND, BRAND_RADIUS, brandAlpha } from '../../theme/brandTokens';
import useBrandPalette from '../../theme/useBrandPalette';

const getGenreLabelKey = (genre) => {
  if (!genre) {
    return null;
  }
  return genre.toString().toLowerCase();
};

const DetailField = ({ label, value }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.25 }}>
      {label}
    </Typography>
    <Typography variant="body1" sx={{ fontWeight: 600, wordBreak: 'break-word' }}>
      {value}
    </Typography>
  </Box>
);

const ConsultationBlock = ({ label, value, emptyLabel }) => (
  <Box>
    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', color: value ? BRAND.text : 'text.secondary' }}>
      {value || emptyLabel}
    </Typography>
  </Box>
);

const StoreCustomerDetailsPage = () => {
  // Concrete hex: StoreCustomerMetricCard runs alpha() on its colour prop.
  const brand = useBrandPalette();
  const { t } = useTranslation();
  const { storeId, customerId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { currentStore } = useAppData();
  const { showSuccess } = useToast();
  const { hasPermission, hasAnyPermission } = usePermissions(storeId);
  // Refunds and the per-customer online payment switch only apply to Pro, which takes payments online.
  const { isPro, entitlements } = useStorePlan(storeId);

  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [upcomingAppointments, setUpcomingAppointments] = useState([]);
  const [upcomingLoading, setUpcomingLoading] = useState(true);
  const [upcomingError, setUpcomingError] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const canUpdate = hasPermission(Permissions.CustomersUpdate);
  const canBookAppointment = hasPermission(Permissions.AppointmentsCreate);
  const canReadServicePackages = hasPermission(Permissions.ServicePackagesRead);
  const canReadConsentForms = hasPermission(Permissions.ConsentFormsRead);
  const canSellServicePackages = hasPermission(Permissions.ServicePackagesSell)
    && Boolean(entitlements?.allowsServicePackages);
  const canOpenAgenda = hasAnyPermission([
    Permissions.TeamMembersAgendaRead,
    Permissions.TeamMembersAgendaReadOwn,
  ]);
  const currencyCode = currentStore?.currency || DEFAULT_CURRENCY;
  const listPath = `/management/stores/${storeId}/customers`;
  const backPath = getSafeManagementReturnPath(searchParams.get('from')) || listPath;

  const loadCustomer = useCallback(async () => {
    if (!storeId || !customerId) {
      setError(t('customersManagement.error.noStoreSelected'));
      setLoading(false);
      return;
    }

    const requestKey = `getStoreCustomer-${storeId}-${customerId}`;
    return requestDeduplicator.deduplicate(requestKey, async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await StoreCustomerService.getStoreCustomer(storeId, customerId);
        setCustomer(data);
      } catch (loadError) {
        console.error('Failed to load store customer:', loadError);
        const status = loadError?.response?.status;
        setError(status === 404
          ? t('customersManagement.details.notFound')
          : (loadError?.response?.data?.message || t('customersManagement.details.loading')));
      } finally {
        setLoading(false);
      }
    });
  }, [customerId, storeId, t]);

  const loadUpcomingAppointments = useCallback(async () => {
    if (!storeId || !customerId) {
      setUpcomingLoading(false);
      return;
    }

    const requestKey = `getStoreCustomerUpcoming-${storeId}-${customerId}`;
    return requestDeduplicator.deduplicate(requestKey, async () => {
      try {
        setUpcomingLoading(true);
        setUpcomingError(null);
        setUpcomingAppointments([]);
        const data = await StoreCustomerService.getUpcomingAppointments(storeId, customerId);
        setUpcomingAppointments(data);
      } catch (loadError) {
        console.error('Failed to load upcoming appointments:', loadError);
        setUpcomingAppointments([]);
        setUpcomingError(t('customersManagement.details.upcoming.error'));
      } finally {
        setUpcomingLoading(false);
      }
    });
  }, [customerId, storeId, t]);

  useEffect(() => {
    loadCustomer();
    loadUpcomingAppointments();
  }, [loadCustomer, loadUpcomingAppointments]);

  const handleSave = async (profile) => {
    try {
      setSaving(true);
      const updated = await StoreCustomerService.updateStoreCustomerProfile(storeId, customerId, profile);
      setCustomer(updated);
      setEditOpen(false);
      showSuccess(t('customersManagement.details.saveSuccess'));
    } catch (saveError) {
      console.error('Failed to save store customer profile:', saveError);
      setError(saveError?.response?.data?.message || t('customersManagement.details.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const handleBookAppointment = () => {
    const slot = getBookingSlotOneHourAhead();
    const bookedCustomerId = customer?.customerId || customerId;
    navigate(buildManagementAppointmentsPath({
      date: slot.date,
      startTime: slot.startTime,
      bookCustomerId: bookedCustomerId,
      from: `/management/stores/${storeId}/customers/${customerId}`,
    }), {
      state: {
        bookCustomer: {
          customerId: bookedCustomerId,
          name: customer?.displayName || '',
          email: customer?.displayEmail || null,
          phone: customer?.displayPhone || null,
        },
      },
    });
  };

  const formatGenre = (genre) => {
    const genreKey = getGenreLabelKey(genre);
    if (!genreKey) {
      return t('common.notProvided');
    }
    return t(`customersManagement.genre.${genreKey}`, { defaultValue: genre });
  };

  if (loading && !customer && !error) {
    return (
      <ManagementPageLayout
        pageTitle={t('customersManagement.details.title')}
        pageSubtitle={t('customersManagement.details.subtitle')}
      >
        <ManagementPageSkeleton />
      </ManagementPageLayout>
    );
  }

  const overlay = customer?.overlay || {};
  const metrics = customer?.metrics || {};
  const customerServicePackages = Array.isArray(customer?.servicePackages) ? customer.servicePackages : [];
  const showServicePackages = canReadServicePackages
    && (Boolean(entitlements?.allowsServicePackages) || customerServicePackages.length > 0);
  const lastVisit = metrics.lastVisitDate
    ? t('customersManagement.details.metrics.lastVisit', { date: formatCalendarDate(metrics.lastVisitDate) })
    : t('customersManagement.details.metrics.noVisits');

  return (
    <ManagementPageLayout
      pageTitle={t('customersManagement.details.title')}
      pageSubtitle={t('customersManagement.details.subtitle')}
    >
      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      {customer ? (
        <ManagementPageCard>
          <Box sx={{ p: { xs: 2, sm: 3 } }}>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={{ xs: 2.5, md: 2 }}
              useFlexGap
              alignItems={{ xs: 'stretch', md: 'flex-start' }}
              justifyContent="space-between"
              sx={{ mb: 3 }}
            >
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                alignItems={{ xs: 'center', sm: 'flex-start' }}
                sx={{ minWidth: 0, flex: 1, textAlign: { xs: 'center', sm: 'left' } }}
              >
                <Avatar
                  src={customer.picture || undefined}
                  sx={{
                    width: { xs: 88, sm: 96 },
                    height: { xs: 88, sm: 96 },
                    bgcolor: brandAlpha('accent', 0.18),
                    border: `2px solid ${brandAlpha('accent', 0.45)}`,
                  }}
                >
                  {customer.displayName?.charAt(0)?.toUpperCase() || <PersonIcon />}
                </Avatar>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="h5" sx={{ fontWeight: 800, color: BRAND.text, wordBreak: 'break-word' }}>
                    {customer.displayName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {customer.displayEmail || t('common.notProvided')}
                    {customer.displayPhone ? ` · ${customer.displayPhone}` : ''}
                  </Typography>
                  {isPro && overlay.onlinePaymentsDisabled ? (
                    <Chip
                      size="small"
                      label={t('customersManagement.details.onlinePayments.badge')}
                      sx={{ mt: 1, fontWeight: 600, bgcolor: alpha('#e11d48', 0.1), color: '#9f1239' }}
                    />
                  ) : null}
                </Box>
              </Stack>
              <Stack
                direction="row"
                spacing={1.5}
                useFlexGap
                flexWrap="wrap"
                sx={{
                  flexShrink: 0,
                  width: { xs: '100%', md: 'auto' },
                }}
              >
                <Button
                  startIcon={<ArrowBackIcon />}
                  onClick={() => navigate(backPath)}
                  variant="outlined"
                  sx={{
                    flex: { xs: 1, md: 'none' },
                    borderColor: BRAND.accent,
                    color: BRAND.accentText,
                    textTransform: 'none',
                    fontWeight: 700,
                    minHeight: 48,
                    borderRadius: BRAND_RADIUS.button,
                  }}
                >
                  {t('common.back')}
                </Button>
                {canUpdate && canBookAppointment ? (
                  <Button
                    onClick={() => setEditOpen(true)}
                    variant="outlined"
                    sx={{
                      flex: { xs: 1, sm: 'none' },
                      borderColor: BRAND.accent,
                      color: BRAND.accentText,
                      textTransform: 'none',
                      fontWeight: 700,
                      minHeight: 48,
                      borderRadius: BRAND_RADIUS.button,
                    }}
                  >
                    {t('customersManagement.details.edit')}
                  </Button>
                ) : null}
                {canUpdate && !canBookAppointment ? (
                  <PrimaryActionButton
                    onClick={() => setEditOpen(true)}
                    sx={{ flex: { xs: 1, md: 'none' }, textTransform: 'none' }}
                  >
                    {t('customersManagement.details.edit')}
                  </PrimaryActionButton>
                ) : null}
                {canBookAppointment ? (
                  <PrimaryActionButton
                    startIcon={<EventAvailableIcon />}
                    onClick={handleBookAppointment}
                    sx={{ flex: { xs: 1, md: 'none' }, textTransform: 'none' }}
                  >
                    {t('customersManagement.details.bookAppointment')}
                  </PrimaryActionButton>
                ) : null}
              </Stack>
            </Stack>

            <Grid container spacing={1.5} sx={{ mb: 3 }}>
              <Grid size={{ xs: 6, md: 3 }}>
                <StoreCustomerMetricCard
                  icon={<FrequencyIcon />}
                  label={t('customersManagement.details.metrics.frequency')}
                  value={metrics.completedVisitCount ?? 0}
                  caption={lastVisit}
                  color="#10b981"
                  delay={0.05}
                />
              </Grid>
              <Grid size={{ xs: 6, md: 3 }}>
                <StoreCustomerMetricCard
                  icon={<NoShowIcon />}
                  label={t('customersManagement.details.metrics.noShows')}
                  value={metrics.noShowCount ?? 0}
                  color="#e11d48"
                  delay={0.1}
                />
              </Grid>
              <Grid size={{ xs: 6, md: 3 }}>
                <StoreCustomerMetricCard
                  icon={<LifetimeIcon />}
                  label={t('customersManagement.details.metrics.lifetimeValue')}
                  value={formatCurrency(metrics.lifetimeValue ?? 0, currencyCode)}
                  color={brand.accent}
                  delay={0.15}
                />
              </Grid>
              <Grid size={{ xs: 6, md: 3 }}>
                <StoreCustomerMetricCard
                  icon={<RecentIcon />}
                  label={t('customersManagement.details.metrics.lastThreeMonths')}
                  value={formatCurrency(metrics.lastThreeMonthsRevenue ?? 0, currencyCode)}
                  color="#3b82f6"
                  delay={0.2}
                />
              </Grid>
              <Grid size={{ xs: 6, md: 3 }}>
                <StoreCustomerMetricCard
                  icon={<ShoppingBagIcon />}
                  label={t('customersManagement.details.metrics.productsSpend')}
                  value={formatCurrency(metrics.productsSpend ?? 0, currencyCode)}
                  color="#a855f7"
                  delay={0.25}
                />
              </Grid>
              {isPro ? (
                <Grid size={{ xs: 6, md: 3 }}>
                  <StoreCustomerMetricCard
                    icon={<RefundIcon />}
                    label={t('customersManagement.details.refunds.metric')}
                    value={formatCurrency(metrics.refundedTotal ?? 0, currencyCode)}
                    caption={t('customersManagement.details.refunds.count', { count: metrics.refundCount ?? 0 })}
                    color="#0369a1"
                    delay={0.3}
                  />
                </Grid>
              ) : null}
            </Grid>

            <Box sx={{ mb: 2 }}>
              <StoreCustomerUpcomingAppointments
                storeId={storeId}
                customerId={customerId}
                appointments={upcomingAppointments}
                loading={upcomingLoading}
                error={upcomingError}
                canOpenAgenda={canOpenAgenda}
              />
            </Box>

            {showServicePackages ? (
              <Box sx={{ mb: 2 }}>
                <StoreCustomerServicePackagesCard
                  storeId={storeId}
                  customerId={customer.customerId || customerId}
                  customerPackages={customerServicePackages}
                  canSell={canSellServicePackages}
                  currencyCode={currencyCode}
                  onChanged={loadCustomer}
                />
              </Box>
            ) : null}

            {/* After leaving Pro the evidence stays readable, but nothing new can be requested. */}
            {canReadConsentForms ? (
              <StoreCustomerConsentsCard
                storeId={storeId}
                customerId={customer.customerId || customerId}
                canSend={Boolean(entitlements?.allowsConsentForms)}
                hideWhenEmpty={!entitlements?.allowsConsentForms}
                sx={{ mb: 2 }}
              />
            ) : null}

            {isPro && customer.refunds?.length ? (
              <Box sx={{ mb: 2 }}>
                <StoreCustomerRefunds refunds={customer.refunds} currencyCode={currencyCode} />
              </Box>
            ) : null}

            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <StoreCustomerSectionCard
                  title={t('customersManagement.details.contact.title')}
                  icon={<PhoneIcon fontSize="small" />}
                >
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <DetailField
                        label={t('customersManagement.details.contact.name')}
                        value={customer.displayName || t('common.notProvided')}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <DetailField
                        label={t('customersManagement.details.contact.email')}
                        value={customer.displayEmail || t('common.notProvided')}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <DetailField
                        label={t('customersManagement.details.contact.phone')}
                        value={customer.displayPhone || t('common.notProvided')}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <DetailField
                        label={t('customersManagement.table.genre')}
                        value={formatGenre(customer.genre)}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <DetailField
                        label={t('customersManagement.table.birthDate')}
                        value={customer.birthDate ? formatCalendarDate(customer.birthDate) : t('common.notProvided')}
                      />
                      <DetailField
                        label={t('vat.fields.number')}
                        value={customer.vatNumber || customer.VatNumber || t('common.notProvided')}
                      />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <DetailField
                        label={t('customersManagement.table.addedOn')}
                        value={formatUtcDate(customer.createdAtUtc)}
                      />
                    </Grid>
                  </Grid>
                </StoreCustomerSectionCard>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <StoreCustomerSectionCard
                  title={t('customersManagement.details.consultation.title')}
                  icon={<ConsultationIcon fontSize="small" />}
                >
                  {(overlay.allergiesAndSensitivities
                    || overlay.medicalConditions
                    || overlay.medications
                    || overlay.pregnancyNursingStatus) ? (
                    <Stack spacing={2}>
                      <ConsultationBlock
                        label={t('customersManagement.details.consultation.allergies')}
                        value={overlay.allergiesAndSensitivities}
                        emptyLabel={t('common.notProvided')}
                      />
                      <ConsultationBlock
                        label={t('customersManagement.details.consultation.medical')}
                        value={overlay.medicalConditions}
                        emptyLabel={t('common.notProvided')}
                      />
                      <ConsultationBlock
                        label={t('customersManagement.details.consultation.medications')}
                        value={overlay.medications}
                        emptyLabel={t('common.notProvided')}
                      />
                      <ConsultationBlock
                        label={t('customersManagement.details.consultation.pregnancy')}
                        value={overlay.pregnancyNursingStatus}
                        emptyLabel={t('common.notProvided')}
                      />
                    </Stack>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      {t('customersManagement.details.consultation.empty')}
                    </Typography>
                  )}
                </StoreCustomerSectionCard>
              </Grid>
            </Grid>
          </Box>
        </ManagementPageCard>
      ) : null}

      {canUpdate ? (
        <StoreCustomerProfileDialog
          open={editOpen}
          onClose={() => setEditOpen(false)}
          customer={customer}
          submitting={saving}
          onSave={handleSave}
          showOnlinePayments={isPro}
        />
      ) : null}
    </ManagementPageLayout>
  );
};

export default StoreCustomerDetailsPage;
